/**
 * Adaptive navigation camera.
 *
 * The requirement driving this module: the driver must SEE which lane to take.
 * Arrow glyphs help, but the thing that actually communicates lanes is the map
 * itself zoomed far enough in that individual lane markings and slip roads are
 * distinguishable, tilted so the road ahead occupies the screen.
 *
 * Strategy:
 *  - Cruise state: zoom scales inversely with speed (fast = zoomed out to see
 *    further ahead), high pitch, camera behind the vehicle.
 *  - Approach state: as distance-to-maneuver falls below a speed-dependent
 *    threshold, zoom ramps smoothly up to junction level (~17.5+) and pitch
 *    drops, giving a clearer top-down read of the lane layout.
 *  - Complex junctions (forks, ramps, roundabouts, maneuvers with lane data)
 *    zoom harder than plain turns.
 *  - After the maneuver the camera relaxes back to cruise.
 *
 * All values are continuous functions of (speed, distance) rather than modes
 * with thresholds, so the camera never visibly "switches" - it breathes.
 */

import type { NavProgress } from '../core/types';

export interface CameraTarget {
  zoom: number;
  pitch: number;
  /** degrees, map bearing = direction of travel */
  bearing: number;
  /** push the vehicle towards the lower third of the screen */
  padding: { top: number; bottom: number; left: number; right: number };
  /** ms the easing should take; roughly the fix interval so motion is continuous */
  duration: number;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
/** Smoothstep - continuous first derivative, no visible kink. */
const smooth = (t: number): number => {
  const x = clamp(t, 0, 1);
  return x * x * (3 - 2 * x);
};

/** Maneuvers where seeing the road layout matters most. */
const COMPLEX_TYPES = new Set(['fork', 'off ramp', 'on ramp', 'roundabout', 'rotary', 'merge']);

export function cruiseZoom(speedMs: number): number {
  // 0 km/h -> 17, 30 km/h -> ~16.3, 60 km/h -> ~15.6, 100+ km/h -> 14.8
  const kmh = speedMs * 3.6;
  return clamp(17 - kmh * 0.022, 14.8, 17);
}

export function computeCamera(
  progress: NavProgress | null,
  heading: number,
  speedMs: number,
  viewportHeight: number,
): CameraTarget {
  const base: CameraTarget = {
    zoom: cruiseZoom(speedMs),
    pitch: 58,
    bearing: heading,
    padding: { top: viewportHeight * 0.42, bottom: 0, left: 0, right: 0 },
    duration: 950,
  };
  if (!progress) return base;

  const d = progress.distanceToManeuver;
  const type = progress.step.maneuver.type;
  const isArrival = type === 'arrive';
  const complex = COMPLEX_TYPES.has(type) || (progress.lanes?.length ?? 0) > 0;

  // Start the zoom-in earlier when driving faster: at 90 km/h you need the
  // junction view ~500 m out; at 30 km/h, 200 m is plenty.
  const approachStart = clamp(180 + speedMs * 3.6 * 3.5, 220, 550);
  // Fully zoomed by this distance.
  const approachEnd = 60;

  if (d >= approachStart || isArrival) return base;

  const t = smooth(1 - (d - approachEnd) / (approachStart - approachEnd)); // 0..1

  // Junction zoom level: complex junctions get closer.
  const junctionZoom = complex ? 17.8 : 17.2;
  // Lower the pitch through the approach - a flatter view separates parallel
  // lanes and slip roads far better than a raked one.
  const junctionPitch = complex ? 32 : 40;

  return {
    zoom: lerp(base.zoom, junctionZoom, t),
    pitch: lerp(base.pitch, junctionPitch, t),
    bearing: heading,
    padding: {
      top: viewportHeight * lerp(0.42, 0.3, t),
      bottom: 0,
      left: 0,
      right: 0,
    },
    duration: 950,
  };
}

/** Overview camera fitting the whole route, for the preview screen. */
export function previewPadding(isPortrait: boolean): {
  top: number;
  bottom: number;
  left: number;
  right: number;
} {
  return isPortrait
    ? { top: 90, bottom: 300, left: 48, right: 48 }
    : { top: 90, bottom: 90, left: 380, right: 60 };
}
